// loyalty.repo — katalog hadiah + kode tukar. Penulisan kode & penukaran lewat RPC (0077),
// bukan insert langsung: kode harus lahir di perangkat pelanggan, dan pemotongan poin harus
// atomik supaya satu kode tak bisa dipakai dua kali.
import { supabase } from '@core/supabase';

export interface Hadiah {
  id: number; nama: string; poin: number; stok: number | null;
  /** Harga perolehan (Rp) — otomatis jadi beban saat hadiah ditukar. 0 = tak dibebankan. */
  nilai_rp: number;
  mulai: string | null; sampai: string | null; aktif: boolean; catatan: string | null;
}
export type HadiahInput = Omit<Hadiah, 'id'>;

export async function listHadiah(hanyaAktif = false): Promise<Hadiah[]> {
  let q = supabase().from('loyalty_rewards').select('*').order('poin');
  if (hanyaAktif) q = q.eq('aktif', true);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []) as Hadiah[];
}

export async function simpanHadiah(id: number | null, d: HadiahInput): Promise<void> {
  const q = supabase().from('loyalty_rewards');
  const { error } = id ? await q.update(d).eq('id', id) : await q.insert(d);
  if (error) throw new Error(error.message);
}

export async function hapusHadiah(id: number): Promise<void> {
  const { error } = await supabase().from('loyalty_rewards').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

/** Token tautan poin pelanggan — dibuat sekali, dipakai ulang tiap CS mengirim tautan. */
export async function tokenPoin(customerId: number): Promise<string> {
  const { data, error } = await supabase().from('customers').select('poin_token').eq('id', customerId).single();
  if (error) throw new Error(error.message);
  const ada = (data as { poin_token: string | null }).poin_token;
  if (ada) return ada;
  const baru = crypto.randomUUID().replace(/-/g, '').slice(0, 20);
  const { error: e2 } = await supabase().from('customers').update({ poin_token: baru }).eq('id', customerId);
  if (e2) throw new Error(e2.message);
  return baru;
}

export interface HasilTukar { ok: boolean; pesan?: string; hadiah?: string; poin?: number; beban?: number; dari_servis?: number; dari_afiliasi?: number }

/**
 * Cocokkan kode dari HP pelanggan → potong poin, catat beban hadiah, kurangi stok.
 * `porsi` hanya untuk PENCATATAN sumber poin — jumlah yang dipotong tetap dari katalog.
 */
export async function tukarDenganKode(customerId: number, kode: string, by: string,
  porsi?: { servis: number; afiliasi: number }): Promise<HasilTukar> {
  const { data, error } = await supabase().rpc('poin_tukar', {
    p_customer: customerId, p_kode: kode.trim(), p_by: by,
    p_dari_servis: porsi?.servis ?? null, p_dari_afiliasi: porsi?.afiliasi ?? null,
  });
  if (error) throw new Error(error.message);
  return (data ?? { ok: false, pesan: 'Tidak ada jawaban dari server.' }) as HasilTukar;
}

/**
 * Catat dari mana poin diambil (servis vs afiliasi) pada penukaran terakhir pelanggan ini.
 * Hanya untuk pembukuan — jumlah poin yang dipotong sudah ditentukan server dari katalog.
 */
export async function catatSumberPoin(customerId: number, poin: number, servis: number, afiliasi: number): Promise<void> {
  const { data, error } = await supabase().from('point_redemptions').select('id')
    .eq('customer_id', customerId).eq('points', poin).is('dari_servis', null)
    .order('created_at', { ascending: false }).limit(1).maybeSingle();
  if (error || !data) return;
  await supabase().from('point_redemptions')
    .update({ dari_servis: servis, dari_afiliasi: afiliasi }).eq('id', (data as { id: number }).id);
}

/** Kode yang masih hidup untuk pelanggan ini — dipakai layar kasir menampilkan sisa waktunya. */
export async function kodeAktif(customerId: number): Promise<{ kode: string; expires_at: string; reward_id: number | null } | null> {
  const { data, error } = await supabase().from('loyalty_codes')
    .select('kode,expires_at,reward_id').eq('customer_id', customerId).is('used_at', null)
    .gt('expires_at', new Date().toISOString()).order('created_at', { ascending: false }).limit(1).maybeSingle();
  if (error) throw new Error(error.message);
  return data as { kode: string; expires_at: string; reward_id: number | null } | null;
}
